import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faMinus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import {
  add,
  addDays,
  differenceInCalendarDays,
  differenceInDays,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { floor } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// DnD provider is supplied by CalendarLayout. Do not nest another provider here.
// import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { JobStatus } from '~/generated/graphql';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useJobNameSync } from '~/hooks/useJobNameSync';
import useWindowDimensions from '~/hooks/useWindowDimensions';
import type {
  FilteredPlannerViewData,
  PlannerViewData,
  ZoomLevel,
} from '~/routes/calendar._index';
import { CalendarSidebar } from '../ui/CalendarSidebar';
import CalendarDayViewDialog from './CalendarDayViewDialog';
import { CalendarEventOrganiser } from './CalendarEventOrganiser';
import {
  DraftJobPill,
  PlannerDateHeader,
  PlannerNavigationArrows,
} from './components';
import DraggableJobWrapper from './DraggableJobWrapper';
import type { CalendarEvent } from './hooks';
import {
  createCellKey,
  createDraftJobKey,
  FloatingPortal,
  useHoverDialog,
  usePlannerDimensions,
  usePlannerEvents,
  usePlannerInitialization,
  usePlannerScroll,
  useRowHeights,
  useSiteExpansion,
} from './hooks';
import { JobDropZone } from './JobDropZone';
import { PlannerToolbar } from './PlannerToolbar';
import { ScheduleEventPill } from './ScheduleEventPill';
import { RescheduleDetails } from './types';
import { useReschedule } from './useReschedule';
import { useDelayedToggle } from './util';

// Re-export CalendarEvent type for use by other components
export type { CalendarEvent };

export type PlannerViewProps = {
  data: FilteredPlannerViewData;
  /** Job clicked */
  onClick: (jobId: string) => void;
  // bounds: { lower: number; upper: number }
  jobDetail: boolean;
  bookingsHidden: boolean;
  scrollDate: Date;
  zoomLevel: ZoomLevel;
  selectedJobId?: string;
  onReschedule?: (details: RescheduleDetails) => void;
  isLoading?: boolean;
  originalDataLength?: number;
  /** Hide the MobileAddButton FAB when viewed on mobile */
  hideMobileFab?: boolean;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  onNavigateToday?: () => void;
  // Zoom controls (optional; when provided, toolbar shows zoom buttons)
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  canZoomIn?: boolean;
  canZoomOut?: boolean;
  /** Called when the visible date changes due to scrolling */
  onVisibleDateChange?: (visibleDate: Date) => void;
  /** Handler for toggling show times */
  onToggleShowTimes?: () => void;
  /** Handler for toggling show bookings */
  onToggleShowBookings?: () => void;
};

const ZOOM_LEVEL_TO_TOP_LEVEL_EVENT_COUNT_MAX: Record<
  ZoomLevel,
  {
    withoutTimes: number;
    withTimes: number;
  }
> = {
  month: {
    withoutTimes: 2,
    withTimes: 1,
  },
  default: {
    withoutTimes: 2,
    withTimes: 2,
  },
  week: {
    withoutTimes: 4,
    withTimes: 4,
  },
} as const;

// Named constants for shared magic numbers
const PLANNER_SCROLL_TOP_KEY = 'planner.scrollTop';

// Type for navigation state passed from CalendarSidebar
interface PlannerLocationState {
  targetSiteId?: string;
}

// Type definitions for better type safety

// Removed obsolete DragItem type (not used)

export function PlannerView({
  data,
  onClick,
  jobDetail,
  bookingsHidden,
  scrollDate,
  zoomLevel,
  selectedJobId,
  onReschedule,
  isLoading = false,
  onNavigatePrev,
  onNavigateNext,
  onNavigateToday,
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  onVisibleDateChange,
  onToggleShowTimes,
  onToggleShowBookings,
}: PlannerViewProps) {
  // Provide a default reschedule handler if parent does not supply one
  const { handleImmediateReschedule } = useReschedule();
  const onRescheduleEffective =
    onReschedule ??
    ((details: RescheduleDetails) => {
      // Fallback: perform immediate reschedule
      handleImmediateReschedule(details);
    });
  // Derived flag not used; rely on hasFilteredData
  const hasFilteredData = Array.isArray(data) && data.length > 0;

  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();

  // Avoid DOM manipulation for global FAB; visibility should be handled via props/context upstream

  const localStorageApi = useLocalStorage();
  const storage = useMemo(
    () => localStorageApi,
    // Keep storage reference stable so dependent callbacks are memo-safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { width: windowWidth } = useWindowDimensions();

  // i18n not currently used locally; keeping hook adds overhead
  // const { t } = useTranslation(['translation', 'job']);
  const parentRef = React.useRef<HTMLDivElement>(null);

  // Toolbar local state synced from props
  const [showTimes, setShowTimes] = useState<boolean>(jobDetail);
  const [showBookings, setShowBookings] = useState<boolean>(!bookingsHidden);

  useEffect(() => setShowTimes(jobDetail), [jobDetail]);
  useEffect(() => setShowBookings(!bookingsHidden), [bookingsHidden]);

  const jobDetailEffective = showTimes;
  const bookingsHiddenEffective = !showBookings;

  // Note: Height cache and lastChangedRow removed - no longer needed with static heights

  // Cache references are now handled by the custom hooks

  // selectedJobId is now passed as a prop from parent component

  // State for job creation cell highlighting
  const [highlightedCells, setHighlightedCells] = useState<Set<string>>(
    new Set()
  );
  const [draftCellId, setDraftCellId] = useState<string | null>(null);

  const topLevelJobCountMax = useMemo(() => {
    return ZOOM_LEVEL_TO_TOP_LEVEL_EVENT_COUNT_MAX[zoomLevel][
      jobDetailEffective ? 'withTimes' : 'withoutTimes'
    ];
  }, [zoomLevel, jobDetailEffective]);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(
    !isMobile
  );

  const [isManuallyExpanded, setIsManuallyExpanded] = useState<boolean>(
    !isMobile
  );

  const { trigger: triggerOpen, clear: clearOpen } = useDelayedToggle(300);
  const { trigger: triggerClose, clear: clearClose } = useDelayedToggle(200);

  // Dynamic date range that expands as user scrolls (infinite scroll behavior)
  const [dateRangeEnd, setDateRangeEnd] = useState<Date>(() => {
    const now = new Date();
    return startOfYear(add(now, { years: 1 }));
  });

  // Gather information about the current date range and index of today
  const { start, count, indexOfToday } = useMemo(() => {
    const now = new Date();
    const start = startOfMonth(add(now, { months: -1 }));

    return {
      start,
      count: differenceInCalendarDays(dateRangeEnd, start),
      indexOfToday: differenceInCalendarDays(now, start),
    };
  }, [dateRangeEnd]);

  // Determine where today is relative to the VISIBLE viewport
  // Moved below columnVirtualizer initialization to avoid temporal dead zone

  // Event processing hook will be defined after columnVirtualizer

  const handleSidebarMouseEnter = () => {
    if (isMobile) return;
    clearClose();
    if (!isSidebarExpanded) {
      triggerOpen(() => setIsSidebarExpanded(true));
    }
  };

  const handleSidebarMouseLeave = () => {
    if (isMobile) return;
    clearOpen();
    if (!isManuallyExpanded) {
      triggerClose(() => setIsSidebarExpanded(false));
    }
  };

  // Site expansion state - extracted to custom hook
  const {
    expandedSiteIds,
    setExpandedSiteIds,
    expandSite,
    collapseSite,
    isSiteExpanded,
  } = useSiteExpansion({ storage });

  // Removed sidebarRef; handled inside CalendarSidebar

  // All dimension calculations - extracted to custom hook
  const {
    headerHeight,
    sideBarWidth,
    collapsedMobileSideBarWidth,
    actualSideBarWidth,
    jobPillHeight,
    expandPillSize,
    overlappingPillOffset,
    jobPillUnderExpandPillMarginRight,
    expandedBookingPillHeight,
    bookingPillHeight,
    jobPillMargin,
    minRowHeight,
    columnWidth,
  } = usePlannerDimensions({
    isMobile,
    isSidebarExpanded,
    windowWidth,
    zoomLevel,
    jobDetailEffective,
    bookingsHiddenEffective,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count,
    getScrollElement: () => parentRef.current,
    estimateSize: () => columnWidth,
    paddingStart: isMobile ? collapsedMobileSideBarWidth : sideBarWidth,
    // Initial value; will be kept in sync via effect
    scrollPaddingStart:
      (isMobile ? collapsedMobileSideBarWidth : sideBarWidth) + columnWidth,
    overscan: 30,
  });

  // Helper: compute the X start offset deterministically using current padding
  const getColumnStart = useCallback(
    (index: number): number => {
      const padding = actualSideBarWidth;
      return index * columnWidth + padding;
    },
    [columnWidth, actualSideBarWidth]
  );

  // Keep paddingStart in sync with actual sidebar width so left edge isn't hidden
  // NOTE: columnVirtualizer is excluded from deps - it's a stable object returned by useVirtualizer.
  // Including it would cause infinite re-runs since the object reference changes on every render.
  useLayoutEffect(() => {
    columnVirtualizer.setOptions({
      ...columnVirtualizer.options,
      paddingStart: actualSideBarWidth,
      // Ensure one extra day (one column) is visible before the target when scrolling
      scrollPaddingStart: actualSideBarWidth + columnWidth,
    } as unknown as Parameters<typeof columnVirtualizer.setOptions>[0]);
    // Recalculate measurements after padding change
    columnVirtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualSideBarWidth, columnWidth]);

  // Update virtualizer count when date range extends
  // NOTE: columnVirtualizer is excluded from deps - it's a stable object returned by useVirtualizer.
  useLayoutEffect(() => {
    columnVirtualizer.setOptions({
      ...columnVirtualizer.options,
      count,
    } as unknown as Parameters<typeof columnVirtualizer.setOptions>[0]);
    columnVirtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // Stabilize virtualizer range to prevent unnecessary recalculations
  // The virtualizer range object is recreated on every render, but we only care about
  // the actual start/end index values. Cache them separately to avoid triggering
  // expensive event filtering when the indices haven't actually changed.
  const virtualizerRange = useMemo(() => {
    const range = columnVirtualizer.range;
    if (!range) return null;
    // Create a stable object that only changes when the indices change
    return { startIndex: range.startIndex, endIndex: range.endIndex };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // We specifically only want to depend on the index values, not the range object itself
    // which is recreated on every render even when indices haven't changed
    columnVirtualizer.range?.startIndex,
    columnVirtualizer.range?.endIndex,
  ]);

  // Event processing - extracted to custom hook for better code organization
  // Provides pre-sorted events, cell/site lookups, and computed vpos for rendering
  const {
    events,
    eventsByCell,
    eventsBySite: eventsBySiteIndex, // Renamed for consistency with existing code
    siteMultiDayJobsMap,
    siteJobCountsMap,
    eventVpos,
    recentOccupied,
  } = usePlannerEvents({
    data,
    start,
    zoomLevel,
    virtualizerRange,
  });

  // Scroll hook: handles scrollToDate, scrollToRevealJob, date range extension, visible date tracking
  const {
    scrollToDate,
    scrollToRevealJob,
    handleTodayClick,
    hasScrolledToRevealForCreationRef,
    isProgrammaticScrollRef,
  } = usePlannerScroll({
    parentRef,
    columnVirtualizer,
    isMobile,
    columnWidth,
    start,
    count,
    dateRangeEnd,
    setDateRangeEnd,
    sideBarWidth,
    collapsedMobileSideBarWidth,
    events,
    onVisibleDateChange,
  });

  // Determine where today is relative to the VISIBLE viewport
  const todayPosition = useMemo(() => {
    const now = new Date();
    const todayDate = startOfDay(now);

    // Use current virtualized column range for viewport bounds
    const { startIndex, endIndex } = columnVirtualizer.range ?? {
      startIndex: 0,
      endIndex: 0,
    };

    const viewportStart = startOfDay(add(start, { days: startIndex }));
    const viewportEnd = startOfDay(add(start, { days: endIndex }));

    if (todayDate < viewportStart) return 'before';
    if (todayDate > viewportEnd) return 'after';
    return 'visible';
  }, [start, columnVirtualizer.range]);

  // Which site currently has a job being dragged if any
  const [draggedSiteId, setDraggedSiteId] = useState<string | null>(null);

  // Which site is currently being hovered over during drag (for temporary expansion)
  const [dragHoverSiteId, setDragHoverSiteId] = useState<string | null>(null);

  // Row height calculations - extracted to custom hook for better code organization
  // Provides height computation functions and caches for virtualizer
  const {
    computeRowHeight,
    heightCache,
    estimateRowSize,
    siteHeights,
    siteTopPrefix,
  } = useRowHeights({
    data,
    expandedSiteIds,
    dragHoverSiteId,
    draggedSiteId,
    draftCellId,
    bookingPillHeight,
    jobPillMargin,
    jobPillHeight,
    minRowHeight,
    siteMultiDayJobsMap,
    siteJobCountsMap,
    start,
    eventsByCell,
    bookingsHiddenEffective,
    jobDetailEffective,
  });

  // Track loading state for add job button
  const [isLoadingJobForm, setIsLoadingJobForm] = useState(false);

  // Reset loading state when form opens (with delay to avoid blinking)
  // Also reset immediately if navigating away from the form route
  useEffect(() => {
    if (!location.pathname.includes('/jobs/planner/new')) {
      setIsLoadingJobForm(false);
      return;
    }
    // Wait for form animation/transition to complete before resetting
    const timer = setTimeout(() => {
      setIsLoadingJobForm(false);
    }, 300); // Match typical transition duration
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Add Job button: open generic create form without preselected site/date
  const handleAddJobClick = useCallback(() => {
    setIsLoadingJobForm(true);
    navigate('/jobs/planner/new');
  }, [navigate]);

  // Toolbar handlers - memoized to prevent PlannerToolbar re-renders
  // Use provided handlers if available, otherwise use local state management
  const handleToggleShowTimes = useCallback(() => {
    if (onToggleShowTimes) {
      onToggleShowTimes();
    } else {
      setShowTimes((v) => !v);
    }
  }, [onToggleShowTimes]);

  const handleToggleShowBookings = useCallback(() => {
    if (onToggleShowBookings) {
      onToggleShowBookings();
    } else {
      setShowBookings((v) => !v);
    }
  }, [onToggleShowBookings]);

  const handleToggleExpandAll = useCallback(() => {
    if (expandedSiteIds.length > 0) {
      setExpandedSiteIds([]);
    } else {
      setExpandedSiteIds(data.map(({ id: siteId }) => siteId));
    }
  }, [expandedSiteIds.length, data, setExpandedSiteIds]);

  // Initialization hook: handles storage cleanup and initial/subsequent date scrolling
  const { hasInitialScrolled } = usePlannerInitialization({
    parentRef,
    columnVirtualizer,
    isLoading,
    scrollDate,
    scrollToDate,
    isProgrammaticScrollRef,
    storage,
  });

  // Update column virtualizer when column width changes (avoid running on initial mount)
  const lastColumnWidthRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    // Skip on initial mount
    if (lastColumnWidthRef.current === null) {
      lastColumnWidthRef.current = columnWidth;
      return;
    }
    // Only act when width actually changes
    if (lastColumnWidthRef.current === columnWidth) {
      return;
    }

    // Get current state before zoom change
    const currentScrollOffset = columnVirtualizer.scrollOffset;
    const currentRange = columnVirtualizer.range;

    if (currentScrollOffset === null || !currentRange) {
      lastColumnWidthRef.current = columnWidth;
      return;
    }

    // Calculate the center date from the virtualizer range BEFORE the change
    const centerIndex = Math.floor(
      (currentRange.startIndex + currentRange.endIndex) / 2
    );

    // Calculate the target scroll offset to maintain the same visual center
    const centerOffset = centerIndex * columnWidth;
    const viewportWidth = window.innerWidth - actualSideBarWidth;
    const targetScrollOffset = centerOffset - viewportWidth / 2;

    // Update column virtualizer WITHOUT shouldAdjustScrollPositionOnItemSizeChange
    // to avoid competing scroll operations
    columnVirtualizer.setOptions({
      ...columnVirtualizer.options,
      estimateSize: () => columnWidth,
      paddingStart: isMobile ? collapsedMobileSideBarWidth : sideBarWidth,
      shouldAdjustScrollPositionOnItemSizeChange: (
        _item: unknown,
        _delta: number,
        instance: unknown
      ) => {
        // Narrow at runtime to guard against internal API changes
        const hasScroll =
          typeof instance === 'object' &&
          instance !== null &&
          'scrollOffset' in instance &&
          'scrollToOffset' in instance &&
          typeof (instance as { scrollToOffset?: unknown }).scrollToOffset ===
            'function';

        if (!hasScroll) {
          return true; // fallback to default adjustment
        }

        const v = instance as {
          scrollOffset: number | null;
          scrollToOffset: (
            offset: number,
            opts?: { behavior?: ScrollBehavior }
          ) => void;
        };

        requestAnimationFrame(() => {
          const finalScrollOffset = v.scrollOffset ?? 0;
          if (Math.abs(finalScrollOffset - targetScrollOffset) > 20) {
            v.scrollToOffset(Math.max(0, targetScrollOffset), {
              behavior: 'auto',
            });
          }
        });
        return true;
      },
    } as unknown as Parameters<typeof columnVirtualizer.setOptions>[0]);

    columnVirtualizer.measure();

    // Single scroll adjustment after measurements are updated
    requestAnimationFrame(() => {
      columnVirtualizer.scrollToOffset(Math.max(0, targetScrollOffset), {
        behavior: 'auto',
      });
    });

    // Update last seen width
    lastColumnWidthRef.current = columnWidth;
    // columnVirtualizer is excluded from deps - it's a stable object returned by useVirtualizer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnWidth, actualSideBarWidth]);

  // Create site index mapping
  const siteRowMap = useMemo(() => {
    return data.reduce((map: Record<string, number>, site, i) => {
      map[site.id] = i;
      return map;
    }, {});
  }, [data]);

  const getSiteIndexById = useCallback(
    (siteId: string) => {
      const index = siteRowMap[siteId] ?? -1;

      return index;
    },
    [siteRowMap]
  );

  const getSiteIdByIndex = useCallback(
    (siteIndex: number) => data[siteIndex]?.id,
    [data]
  );

  // isSiteExpanded is now provided by useSiteExpansion hook

  // Simple preview vpos function
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getPreviewVpos = useCallback(
    (siteIndex: number, startIndex: number, endIndex: number) => {
      let candidateVpos = 0;

      const rangeOccupied = (
        siteIndex: number,
        startIndex: number,
        endIndex: number,
        vpos: number
      ) => {
        for (let i = startIndex; i <= endIndex; i++) {
          if (recentOccupied[siteIndex]?.[i]?.[vpos]) {
            return true;
          }
        }
        return false;
      };

      while (rangeOccupied(siteIndex, startIndex, endIndex, candidateVpos)) {
        candidateVpos++;
        if (
          candidateVpos === 1 &&
          !isSiteExpanded(getSiteIdByIndex(siteIndex))
        ) {
          break;
        }
      }

      return candidateVpos;
    },
    [getSiteIdByIndex, isSiteExpanded, recentOccupied]
  );

  // Lazy expand buttons calculation - only computes for visible rows
  // This replaces the previous O(n²) useMemo that computed for ALL rows upfront
  // Results are cached in a ref to avoid recomputation when scrolling
  type ExpandButtonData = {
    siteId: string;
    columnIndex: number;
    expandCount: number;
    eventId: string;
    isMultiDayEvent: boolean;
    totalJobsInCell: number;
    lastVisiblePillVpos: number;
  };

  // Cache ref for expand buttons - stores computed results keyed by siteIndex
  const expandButtonsCacheRef = useRef<Map<number, ExpandButtonData[]>>(
    new Map()
  );

  const getExpandButtonsForRow = useCallback(
    (siteIndex: number): ExpandButtonData[] => {
      // Check cache first - returns cached result if available
      const cached = expandButtonsCacheRef.current.get(siteIndex);
      if (cached !== undefined) return cached;

      const siteId = getSiteIdByIndex(siteIndex);
      if (!siteId) return [];

      const siteEvents = eventsBySiteIndex.get(siteIndex) || [];
      if (siteEvents.length === 0) return [];

      const isExpanded = expandedSiteIds.includes(siteId);
      const buttons: ExpandButtonData[] = [];

      // Track which columns we've already processed to avoid duplicates
      const processedColumns = new Set<number>();

      for (const { x: columnIndex, event, compositeKey } of siteEvents) {
        if (event.status === 'Booking') continue;

        const days = CalendarEventOrganiser.getCalendarEventLength(event);
        const isMultiDayEvent = days > 1;
        const eventKey = compositeKey || event.id;

        // Only process top-level events (vpos === 0)
        if (eventVpos[eventKey] !== 0) continue;

        for (let i = columnIndex; i <= columnIndex + days - 1; i++) {
          // Skip if we've already processed this column
          if (processedColumns.has(i)) continue;
          processedColumns.add(i);

          // Use eventsByCell for O(1) lookup instead of filtering all events
          const targetDate = addDays(start, i);
          const cellKey = createCellKey(siteId, targetDate);
          const cellEvents = eventsByCell.get(cellKey) || [];
          const jobsInCell = cellEvents.filter(
            (ev) => ev.event.status !== 'Booking'
          );
          const totalJobsInCell = jobsInCell.length;

          const expandCount =
            totalJobsInCell - (isMultiDayEvent ? 1 : topLevelJobCountMax);

          if (expandCount >= 1) {
            // Calculate last visible pill position
            const lastVisiblePillVpos = isExpanded
              ? Math.min(
                  totalJobsInCell - 1,
                  topLevelJobCountMax + expandCount - 1
                )
              : topLevelJobCountMax - 1;

            buttons.push({
              siteId,
              columnIndex: i,
              expandCount,
              eventId: event.id,
              isMultiDayEvent,
              totalJobsInCell,
              lastVisiblePillVpos,
            });
          }
        }
      }

      // Cache result before returning to avoid recomputation on scroll
      expandButtonsCacheRef.current.set(siteIndex, buttons);
      return buttons;
    },
    [
      getSiteIdByIndex,
      eventsBySiteIndex,
      eventsByCell,
      expandedSiteIds,
      eventVpos,
      topLevelJobCountMax,
      start,
    ]
  );

  // Track previous function reference to detect when deps change
  // When useCallback recreates the function (due to dep changes), we clear the cache
  // NOTE: This intentionally clears cache during render (before it's used), not in useEffect (after render)
  const prevGetExpandButtonsRef = useRef(getExpandButtonsForRow);
  if (prevGetExpandButtonsRef.current !== getExpandButtonsForRow) {
    expandButtonsCacheRef.current.clear();
    prevGetExpandButtonsRef.current = getExpandButtonsForRow;
  }

  // Note: calculateDynamicHeight removed - using static heights for all cases including drag
  // siteHeights, siteTopPrefix are now provided by useRowHeights hook

  const siteTop = useCallback(
    (siteId: string) => {
      const siteIndex = getSiteIndexById(siteId);
      if (siteIndex < 0) return 0;
      return siteTopPrefix[siteIndex] ?? 0;
    },
    [getSiteIndexById, siteTopPrefix]
  );

  // Memoize total height calculation to prevent unnecessary recalculations
  const totalHeight = useMemo(() => {
    const sumSiteHeights = siteHeights.reduce((acc, h) => acc + h, 0);
    return headerHeight + sumSiteHeights;
  }, [headerHeight, siteHeights]);

  // Create rowVirtualizer after memoized functions are available
  // heightCache and estimateRowSize are now provided by useRowHeights hook

  const rowVirtualizer = useVirtualizer({
    paddingEnd: headerHeight,
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: estimateRowSize, // Use callback instead of static value
    overscan: 5, // Increase overscan to reduce measurement frequency
    // Remove measureElement completely to prevent measurement loops
    getItemKey: (index) => {
      const siteId = data[index]?.id;
      const isExpanded = expandedSiteIds.includes(siteId || '');
      return `${siteId}-${isExpanded}`;
    },
  });

  // Keep virtualizer measurements in sync when a row is temporarily expanded by drag-hover
  // NOTE: rowVirtualizer is intentionally excluded from deps as it's a stable object reference
  // and including it would cause unnecessary re-measurements on every render.
  useEffect(() => {
    rowVirtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowVirtualizer is stable; only re-measure when dragHoverSiteId changes
  }, [dragHoverSiteId]);

  // Scroll restoration: restore on mount, save on scroll/visibility change
  const didRestoreScrollRef = useRef(false);

  // Restore scroll position
  useEffect(() => {
    if (
      !hasInitialScrolled ||
      !hasFilteredData ||
      didRestoreScrollRef.current
    ) {
      return;
    }

    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    didRestoreScrollRef.current = true;

    const navState = location.state as PlannerLocationState | null;
    const targetSiteId = navState?.targetSiteId;

    // Priority 1: Navigation state has targetSiteId (from Calendar sidebar click)
    if (targetSiteId) {
      const siteIndex = getSiteIndexById(targetSiteId);
      if (siteIndex >= 0) {
        const targetScrollTop = siteTop(targetSiteId);
        requestAnimationFrame(() => {
          scrollElement.scrollTo({ top: targetScrollTop, behavior: 'auto' });
        });
        return;
      }
    }

    // Priority 2: Restore stored scrollTop
    const storedTop = sessionStorage.getItem(PLANNER_SCROLL_TOP_KEY);
    if (storedTop !== null) {
      const scrollTop = Number(storedTop);
      if (Number.isFinite(scrollTop) && scrollTop > 0) {
        requestAnimationFrame(() => {
          scrollElement.scrollTo({ top: scrollTop, behavior: 'auto' });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitialScrolled, hasFilteredData]);

  // Save scroll position on scroll (debounced) and visibility change
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const saveScrollPosition = () => {
      if (!didRestoreScrollRef.current) return;
      const top = parentRef.current?.scrollTop;
      if (top !== undefined && Number.isFinite(top)) {
        sessionStorage.setItem(PLANNER_SCROLL_TOP_KEY, `${top}`);
      }
    };

    const handleScroll = () => {
      if (!didRestoreScrollRef.current) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(saveScrollPosition, 200);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveScrollPosition();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      scrollElement.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Sidebar height should be created AFTER heightCache to avoid temporal dead zone
  const sidebarSiteHeight = useCallback(
    (siteId: string) => {
      const cached = heightCache.get(siteId);
      if (cached !== undefined) return cached;
      // Compute collapsed height on demand using single source
      return computeRowHeight(siteId, { expanded: false });
    },
    [heightCache, computeRowHeight]
  );

  const handleEventClick = (eventStatus: string, eventId: string) => {
    // Use scroll to reveal on desktop
    scrollToRevealJob(eventId);

    if (eventStatus === 'Booking') {
      onClick(`bookings/${eventId}`);
    } else if (eventStatus === JobStatus.Created) {
      onClick(`${eventId}/edit`);
    } else {
      onClick(eventId);
    }
  };

  // Memoize the selected job pill data to prevent unnecessary re-renders
  const selectedJobPillData = useMemo(() => {
    if (!selectedJobId) return null;

    // Find the selected job in events
    const selectedEvent = events.find(
      ({ event }) => event.id === selectedJobId
    );
    if (!selectedEvent) return null;

    return {
      jobId: selectedJobId,
      siteId: selectedEvent.siteId,
      columnIndex: selectedEvent.x,
      siteIndex: selectedEvent.y,
    };
  }, [selectedJobId, events]);

  // Hover dialog for job pills - using Floating UI
  const {
    hoveredJobPill,
    setHoveredJobPill,
    handleJobPillMouseEnter,
    handleJobPillMouseLeave,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    floatingStyles,
    refs: floatingRefs,
  } = useHoverDialog({
    parentRef,
    isMobile,
  });

  // Long press state for creating jobs
  // Long press meta not consumed outside of setter; defer state until needed

  // Track clicked cell for visual feedback
  const [clickedCell, setClickedCell] = useState<{
    siteId: string;
    date: Date;
  } | null>(null);

  // Track desktop draft job pill state
  const [desktopDraftJobPill, setDesktopDraftJobPill] = useState<{
    siteId: string;
    date: Date;
    columnIndex: number;
    siteIndex: number;
  } | null>(null);

  // Track draft job name from local storage using the new hook
  const jobName = useJobNameSync(
    draftCellId ? `draft.job.${draftCellId}` : null
  );

  // Track if we're currently in job creation flow
  const [isInJobCreationFlow, setIsInJobCreationFlow] = useState(false);

  // Track when second cell was clicked for timeout-based clearing
  const secondCellClickTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  // Parse URL parameters for job creation when on form
  const urlJobCreation = useMemo(() => {
    if (!location.pathname.includes('/jobs/planner/new')) return null;

    const urlParams = new URLSearchParams(location.search);
    const siteId = urlParams.get('siteId');
    const dateParam = urlParams.get('date');

    if (!siteId || !dateParam) return null;

    return {
      siteId,
      date: dateParam,
      parsedDate: new Date(dateParam),
    };
  }, [location.pathname, location.search]);

  // Get storage key for current job creation cell
  // Helper kept inline via createDraftJobKey; no separate getter needed

  // Track the last storage key to prevent infinite loops
  // lastStorageKeyRef unused with current storage sync approach

  // Create setJobNameWithEvent function
  const setJobNameWithEvent = useCallback(
    (key: string, value: string) => {
      try {
        storage.set(key, value);
        window.dispatchEvent(
          new CustomEvent('jobNameStorageChanged', {
            detail: { key, value },
          })
        );
      } catch (error) {
        console.error('Failed to update job name storage:', error);
      }
    },
    [storage]
  );

  // Effect to handle URL-driven job creation state
  // NOTE: We intentionally exclude expandedSiteIds from dependencies to prevent infinite loop.
  // The expandSite function already checks if the site is expanded before adding it,
  // so we only need to call it when urlJobCreation changes.
  //
  // IMPORTANT: setState functions are excluded from deps as they are stable references
  // and including them can cause unnecessary re-runs. We only need to track the actual
  // values that should trigger this effect: pathname and urlJobCreation string values.
  const urlJobCreationKey = urlJobCreation
    ? `${urlJobCreation.siteId}:${urlJobCreation.date}`
    : null;

  // Track the last processed urlJobCreationKey to prevent redundant scroll calls
  const lastProcessedUrlJobCreationKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const isJobCreationForm = location.pathname.includes('/jobs/planner/new');

    if (isJobCreationForm && urlJobCreation) {
      const cellId = createCellKey(urlJobCreation.siteId, urlJobCreation.date);

      // Only update state if values have actually changed to prevent loops
      setDraftCellId((prev) => (prev === cellId ? prev : cellId));
      setHighlightedCells((prev) => {
        const newSet = new Set([cellId]);
        // Check if the set actually changed to avoid unnecessary re-renders
        if (prev.size === 1 && prev.has(cellId)) return prev;
        return newSet;
      });
      setIsInJobCreationFlow((prev) => (prev === true ? prev : true));

      // Expand the row if it's collapsed to make space for the job pill
      // expandSite already handles the check for whether site is already expanded
      expandSite(urlJobCreation.siteId);

      // Scroll to reveal the cell after form is open
      // Only scroll if this is a new urlJobCreationKey to prevent repeated scrolls
      if (
        urlJobCreation.date &&
        urlJobCreation.siteId &&
        lastProcessedUrlJobCreationKeyRef.current !== urlJobCreationKey
      ) {
        const date = new Date(urlJobCreation.date);
        scrollToRevealJob(date, urlJobCreation.siteId, true); // Mark as job creation scroll
        lastProcessedUrlJobCreationKeyRef.current = urlJobCreationKey;
      }
    } else {
      // Only reset state if we're actually leaving job creation mode
      setDraftCellId((prev) => (prev === null ? prev : null));
      setHighlightedCells((prev) => (prev.size === 0 ? prev : new Set()));
      setDesktopDraftJobPill((prev) => (prev === null ? prev : null));
      setIsInJobCreationFlow((prev) => (prev === false ? prev : false));
      setHiddenButtonCell((prev) => (prev === null ? prev : null));
      // Reset the scroll flag when exiting job creation flow
      hasScrolledToRevealForCreationRef.current = false;
      lastProcessedUrlJobCreationKeyRef.current = null;
    }
    // We depend on urlJobCreationKey (string) instead of urlJobCreation (object)
    // to prevent re-runs when object reference changes but values are the same.
    // setState functions are intentionally excluded as they're stable references.
    // scrollToRevealJob and expandSite are also excluded as they're stable callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, urlJobCreationKey]);

  // Effect to handle navigation between cells (Discard/Keep Changes)
  // IMPORTANT: This effect handles cell transitions during job creation.
  // We use string comparison to prevent infinite loops from object reference changes.
  useEffect(() => {
    if (!urlJobCreation || !draftCellId) return;

    const newCellId = createCellKey(urlJobCreation.siteId, urlJobCreation.date);

    if (lastUrlKeyRef.current && lastUrlKeyRef.current !== newCellId) {
      if (draftCellId !== newCellId) {
        // Discard: clear old cell, highlight new
        if (secondCellClickTimeoutRef.current) {
          clearTimeout(secondCellClickTimeoutRef.current);
          secondCellClickTimeoutRef.current = null;
        }
        setJobNameWithEvent(
          createDraftJobKey(
            draftCellId.split(':')[0],
            draftCellId.split(':')[1]
          ),
          ''
        );
        setDraftCellId(newCellId);
        setHighlightedCells(new Set([newCellId]));
        setJobNameWithEvent(
          createDraftJobKey(newCellId.split(':')[0], newCellId.split(':')[1]),
          ''
        );
      } else if (highlightedCells.has(newCellId)) {
        // Keep Changes: keep original cell, clear new
        setHighlightedCells(new Set([draftCellId]));
        setJobNameWithEvent(
          createDraftJobKey(newCellId.split(':')[0], newCellId.split(':')[1]),
          ''
        );
        // Only clear desktopDraftJobPill if we're not in job creation flow
        if (
          !isInJobCreationFlow &&
          desktopDraftJobPill &&
          desktopDraftJobPill.siteId === urlJobCreation.siteId &&
          format(desktopDraftJobPill.date, 'yyyy-MM-dd') === urlJobCreation.date
        ) {
          setDesktopDraftJobPill(null);
        }
      }
    }

    lastUrlKeyRef.current = newCellId;
    // We use urlJobCreationKey (computed above) instead of urlJobCreation object
    // to prevent re-runs when object reference changes. We stringify desktopDraftJobPill
    // to avoid object reference comparison issues.
    // setState functions are intentionally excluded as they're stable references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlJobCreationKey,
    draftCellId,
    highlightedCells,
    isInJobCreationFlow,
    desktopDraftJobPill,
  ]);

  const handleCellClick = useCallback(
    (siteId: string, date: Date) => {
      // Navigate to DayView outlet for this site and date
      const dateString = format(date, 'yyyy-MM-dd');
      navigate(`${siteId}/day?date=${dateString}`);
    },
    [navigate]
  );

  const handleLongPress = useCallback(
    (siteId: string, date: Date) => {
      if (isMobile) {
        const cellId = createCellKey(siteId, date);

        // Check if we already have a draft in a different cell
        const hasExistingDraft = draftCellId && draftCellId !== cellId;

        if (hasExistingDraft) {
          // Second cell long pressed - add to highlighted cells, keep pill in original cell
          setHighlightedCells(new Set([draftCellId, cellId]));
        } else {
          // First cell long pressed or switching to new cell
          setDraftCellId(cellId);
          setHighlightedCells(new Set([cellId]));

          // Set new cell storage to empty
          setJobNameWithEvent(createDraftJobKey(siteId, date), '');
        }

        navigate(
          `/jobs/planner/new?siteId=${siteId}&date=${format(
            date,
            'yyyy-MM-dd'
          )}`
        );
        return;
      }

      // On desktop, show bottom sheet as before (no-op placeholder, state removed)
    },
    [
      isMobile,
      navigate,
      setDraftCellId,
      setHighlightedCells,
      setJobNameWithEvent,
      draftCellId,
    ]
  );

  // Clear clicked cell when navigating away from job form
  useEffect(() => {
    // Clear clicked cell if:
    // 1. Not on a job form route (doesn't contain '/new')
    // 2. Not on a job calendar route (to handle back navigation)
    // 3. On a different route entirely
    const isJobFormRoute = location.pathname.includes('/new');
    const isJobCalendarRoute = location.pathname.includes('/jobs/planner');

    if (!isJobFormRoute || !isJobCalendarRoute) {
      setClickedCell(null);
    }
  }, [location.pathname]);

  // Consolidated cleanup effect - runs only on unmount
  // Combines multiple cleanup operations to reduce effect overhead
  useEffect(() => {
    return () => {
      // Clear job creation scroll tracking ref
      hasScrolledToRevealForCreationRef.current = false;

      // Reset cell selection state
      // Note: Only clears state on component unmount, not when pill is hidden
      // This allows the job name to persist through the creation flow
      setClickedCell(null);
      setDesktopDraftJobPill(null);
    };
  }, []);

  // In PlannerView, add a state to track the last cell for which the button should be hidden
  const [hiddenButtonCell, setHiddenButtonCell] = useState<{
    siteId: string;
    date: string;
  } | null>(null);

  // Track last URL key to detect cell switches
  const lastUrlKeyRef = useRef<string | null>(null);

  // Pre-calculate cell coordinates indexed by column for O(1) lookup during render
  // This eliminates the expensive filter operation that was O(n) per column
  // NOTE: We don't cache 'site' references to avoid stale data when jobs are updated
  const columnVirtualItems = columnVirtualizer.getVirtualItems();
  const rowVirtualItems = rowVirtualizer.getVirtualItems();

  // Pre-index cells by column index for O(1) lookup in render loops
  const cellsByColumnIndex = useMemo(() => {
    const cellMap = new Map<
      number,
      Array<{
        column: (typeof columnVirtualItems)[number];
        row: (typeof rowVirtualItems)[number];
        siteIndex: number;
        cellDate: Date;
        cellKey: string;
      }>
    >();

    columnVirtualItems.forEach((column) => {
      const columnCells = rowVirtualItems.map((row) => {
        const cellDate = add(start, { days: column.index });
        return {
          column,
          row,
          siteIndex: row.index,
          cellDate,
          cellKey: createCellKey(data[row.index].id, cellDate),
        };
      });
      cellMap.set(column.index, columnCells);
    });

    return cellMap;
  }, [columnVirtualItems, rowVirtualItems, data, start]);

  const gridTotalHeight = rowVirtualizer.getTotalSize();
  const gridBodyHeight = Math.max(0, gridTotalHeight - headerHeight);

  // Ensure grid fills available viewport height of the scroll container
  const [availableHeight, setAvailableHeight] = useState(0);
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const update = () => setAvailableHeight(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const gridRenderHeight = Math.max(gridTotalHeight, availableHeight);
  const gridBodyRenderHeight = Math.max(
    gridBodyHeight,
    Math.max(0, availableHeight - headerHeight)
  );
  const sidebarSites = hasFilteredData ? data : [];

  return (
    <>
      {/* Reschedule offered/accepted job dialog */}
      {/* TODO this shouldn't be here as part of this render loop, move it up and capture an event to handle rescheduling */}

      <div
        className={classNames(
          'relative transition-opacity duration-150',
          isLoading || !hasInitialScrolled ? 'opacity-0' : 'opacity-100'
        )}
      >
        {/* Navigation arrows with Today button (desktop only) - extracted component */}
        {!isMobile && (
          <PlannerNavigationArrows
            todayPosition={todayPosition}
            actualSideBarWidth={actualSideBarWidth}
            onNavigatePrev={onNavigatePrev}
            onNavigateNext={onNavigateNext}
            onNavigateToday={onNavigateToday}
            onTodayClick={handleTodayClick}
          />
        )}
        {/* Unified responsive toolbar - extracted as memoized component for performance */}
        <PlannerToolbar
          showTimes={showTimes}
          onToggleShowTimes={handleToggleShowTimes}
          showBookings={showBookings}
          onToggleShowBookings={handleToggleShowBookings}
          hasExpandedSites={expandedSiteIds.length > 0}
          onToggleExpandAll={handleToggleExpandAll}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
          onAddJob={handleAddJobClick}
          isAddJobLoading={isLoadingJobForm}
        />
        <div
          data-usetiful='planner-view-grid'
          ref={parentRef}
          style={{
            overflow: 'auto',
            width: '100%',
            height: 'calc(100vh - 5rem - 4rem - 4rem)',
            // transform: 'rotate(180deg)',
          }}
        >
          <div
            className='bg-[#FBFBFB] min-h-screen-minus-nav'
            style={{
              width: columnVirtualizer.getTotalSize(),
              height: gridRenderHeight,
              position: 'relative',
            }}
          >
            {/* Draw grid lines and cell bg colours - OPTIMIZED with O(1) lookup */}
            {columnVirtualizer.getVirtualItems().map((column) => {
              // O(1) lookup instead of O(n) filter
              const columnCells = cellsByColumnIndex.get(column.index) || [];

              return (
                <div
                  key={`grid-index-${column.key}`}
                  className={classNames(
                    column.index === indexOfToday && 'bg-[#EB00FF1A]',
                    'pointer-events-none '
                  )}
                  style={{
                    position: 'absolute',
                    top: headerHeight,
                    left: 0,
                    height: rowVirtualizer.getTotalSize() - headerHeight,
                    width: column.size,
                    transform: `translateX(${column.start}px)`,
                  }}
                >
                  {columnCells.map((cellData) => {
                    // Get fresh site data (not cached to avoid stale references)
                    const site = data[cellData.siteIndex];

                    // Calculate dynamic state (not cached to avoid stale closures)
                    const isCellClicked = Boolean(
                      clickedCell &&
                        clickedCell.siteId === site.id &&
                        cellData.cellDate.getTime() ===
                          clickedCell.date.getTime()
                    );

                    const isCreatingJobCell = urlJobCreation
                      ? (urlJobCreation.siteId === site.id &&
                          urlJobCreation.date ===
                            format(cellData.cellDate, 'yyyy-MM-dd')) ||
                        highlightedCells.has(cellData.cellKey)
                      : highlightedCells.has(cellData.cellKey);

                    // Check if DayView is open for this cell
                    const isDayViewOpen = (() => {
                      if (!location.pathname.includes('/day')) return false;
                      const urlParams = new URLSearchParams(location.search);
                      const dayViewDate = urlParams.get('date');
                      if (!dayViewDate) return false;
                      const cellDateKey = format(
                        cellData.cellDate,
                        'yyyy-MM-dd'
                      );
                      // Check if this cell matches the DayView date and site
                      const pathParts = location.pathname.split('/');
                      const dayIndex = pathParts.indexOf('day');
                      const siteIdFromPath =
                        dayIndex > 0
                          ? decodeURIComponent(pathParts[dayIndex - 1])
                          : null;
                      return (
                        dayViewDate === cellDateKey &&
                        (siteIdFromPath === site.id ||
                          siteIdFromPath === site.name)
                      );
                    })();

                    return (
                      <div
                        key={String(cellData.row.key)}
                        className={classNames(
                          'border-b border-r border-x-[#E0E0E0] border-y-outline-variant',
                          (isCellClicked || isCreatingJobCell) &&
                            ' lg:bg-[#0070701A]',
                          isDayViewOpen &&
                            column.index !== indexOfToday &&
                            '!bg-[#0070701A]',
                          isDayViewOpen &&
                            column.index === indexOfToday &&
                            'bg-[#ea00ff31]'
                        )}
                        style={{
                          position: 'absolute',
                          top: cellData.row.start,
                          left: 0,
                          height: cellData.row.size,
                          width: column.size,
                        }}
                      >
                        {!bookingsHiddenEffective && (
                          <div
                            className={classNames(
                              column.index === indexOfToday
                                ? 'bg-[#E7D2E9]'
                                : 'bg-[#EEEEEE]',
                              (isCellClicked || isCreatingJobCell) &&
                                'bg-[#00707033]'
                            )}
                            style={{
                              height: bookingPillHeight,
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Add drop zones - OPTIMIZED with O(1) lookup */}
            {columnVirtualizer.getVirtualItems().map((column) => {
              // O(1) lookup instead of O(n) filter
              const columnDropCells =
                cellsByColumnIndex.get(column.index) || [];

              return (
                <div
                  key={`drop-zones-column-index-${column.key}`}
                  style={{
                    position: 'absolute',
                    top: headerHeight,
                    left: 0,
                    height: gridBodyRenderHeight,
                    width: column.size,
                    transform: `translateX(${column.start}px)`,
                  }}
                >
                  {columnDropCells.map((cellData) => {
                    // Get fresh site data (not cached to avoid stale references)
                    const site = data[cellData.siteIndex];

                    // Calculate dynamic data (not cached to avoid stale closures)
                    const cellEvents = eventsByCell.get(cellData.cellKey) || [];
                    const isExpanded = expandedSiteIds.includes(site.id);

                    return (
                      <div
                        key={`cell-${site.id}-${format(
                          cellData.cellDate,
                          'yyyy-MM-dd'
                        )}`}
                        style={{
                          position: 'absolute',
                          top: cellData.row.start,
                          left: 0,
                          height: cellData.row.size,
                          width: '100%',
                        }}
                      >
                        <JobDropZone
                          key={`${site.id}-${format(
                            cellData.cellDate,
                            'yyyy-MM-dd'
                          )}`}
                          siteId={site.id}
                          siteName={site.name}
                          date={cellData.cellDate}
                          dataUsetiful={
                            cellData.siteIndex === 1 &&
                            cellData.column.index === indexOfToday + 1
                              ? 'planner-view-dropzone-tomorrow'
                              : undefined
                          }
                          cellEvents={cellEvents}
                          isRowExpanded={isExpanded}
                          onDragHover={setDragHoverSiteId}
                          previewTop={() => {
                            // For ghost pill, position at bottom of existing job pills
                            const visibleJobs = cellEvents.filter(
                              ({ event }) => event.status !== 'Booking'
                            );
                            const ghostPillVpos = visibleJobs.length; // Position after existing pills

                            const top =
                              bookingPillHeight +
                              (jobPillHeight + jobPillMargin) * ghostPillVpos +
                              jobPillMargin;

                            return top;
                          }}
                          onReschedule={onRescheduleEffective}
                          onCellClick={handleCellClick}
                          navigate={navigate}
                          bookingsHidden={bookingsHiddenEffective}
                          jobDetail={jobDetailEffective}
                          onLongPress={handleLongPress}
                          setDraftCellId={setDraftCellId}
                          setHighlightedCells={setHighlightedCells}
                          highlightedCells={highlightedCells}
                          draftCellId={draftCellId}
                          setJobNameWithEvent={setJobNameWithEvent}
                          setDesktopDraftJobPill={setDesktopDraftJobPill}
                          start={start}
                          data={data}
                          storage={storage}
                          hiddenButtonCell={hiddenButtonCell}
                          setHiddenButtonCell={setHiddenButtonCell}
                          setIsInJobCreationFlow={setIsInJobCreationFlow}
                          location={location}
                          onScrollToReveal={scrollToRevealJob}
                          secondCellClickTimeoutRef={secondCellClickTimeoutRef}
                          expandSite={expandSite}
                        >
                          <div
                            className='flex h-full flex-col'
                            style={{
                              minHeight: cellData.row.size,
                            }}
                          />
                        </JobDropZone>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Render events - OPTIMIZED with O(1) lookup */}
            {rowVirtualizer.getVirtualItems().flatMap((row) => {
              // O(1) lookup instead of O(n) filter
              const siteEvents = eventsBySiteIndex.get(row.index) || [];

              /** `vposesOverlappedByExpandButtons[columnIndex].has(vpos) === true` if this vpos is overlapped by an expand button */
              const vposesOverlappedByExpandButtons: Record<
                number,
                Set<number>
              > = {};

              /** `encounteredTopLevelMultiDayEvent[columnIndex] === true` if we should not render any more jobs in this column */
              const encounteredTopLevelMultiDayJob: boolean[] = [];

              return siteEvents.map(
                ({ x: columnIndex, event, siteId, compositeKey }) => {
                  // Ignore bookings if they are hidden
                  if (event.status === 'Booking' && bookingsHiddenEffective)
                    return null;

                  if (
                    event.status !== 'Booking' &&
                    encounteredTopLevelMultiDayJob[columnIndex]
                  )
                    return null;

                  const eventKey = compositeKey || event.id;
                  const baseVpos =
                    event.status === 'Booking' ? 0 : eventVpos[eventKey];

                  const thisEventStart = startOfDay(new Date(event.start));
                  const days =
                    CalendarEventOrganiser.getCalendarEventLength(event);
                  const isMultiDayEvent = days > 1;

                  // Check if there's a draft pill that overlaps with this job (handles multi-day jobs properly)
                  const hasDraftPillOverlap = (() => {
                    if (
                      !urlJobCreation ||
                      event.status === 'Booking' ||
                      urlJobCreation.siteId !== siteId
                    ) {
                      return false;
                    }

                    const draftColumnIndex = differenceInCalendarDays(
                      urlJobCreation.parsedDate,
                      start
                    );

                    // Check if the draft pill overlaps with any day of this multi-day job
                    const jobStartColumn = columnIndex;
                    const jobEndColumn = columnIndex + days - 1;

                    return (
                      draftColumnIndex >= jobStartColumn &&
                      draftColumnIndex <= jobEndColumn
                    );
                  })();

                  // Adjust vpos: if draft pill overlaps with this job, push it down by 1
                  // This ensures draft pill stays at top and existing jobs shift down, respecting 3-job limit
                  const vpos =
                    hasDraftPillOverlap && baseVpos !== undefined
                      ? baseVpos + 1
                      : baseVpos;

                  // ? vposes have not been calculated yet?
                  if (vpos === undefined) {
                    return null;
                  }

                  const isExpanded = isSiteExpanded(siteId);

                  if (vpos >= topLevelJobCountMax && !isExpanded) {
                    return null; // cell is full
                  }

                  if (
                    vpos === 0 &&
                    isMultiDayEvent &&
                    !isExpanded &&
                    event.status !== 'Booking'
                  ) {
                    for (
                      let i = columnIndex;
                      i <= columnIndex + days - 1;
                      i++
                    ) {
                      encounteredTopLevelMultiDayJob[i] = true;
                    }
                  }

                  // Add expand button if there are multiple jobs in the same row/col
                  // Use baseVpos (before draft pill adjustment) for expand button calculation
                  // OPTIMIZED: Use pre-computed eventsByCell for O(1) lookup instead of filtering all events
                  if (event.status !== 'Booking' && baseVpos === 0) {
                    for (
                      let i = columnIndex;
                      i <= columnIndex + days - 1;
                      i++
                    ) {
                      const targetDate = addDays(
                        thisEventStart,
                        i - columnIndex
                      );
                      const cellKey = createCellKey(siteId, targetDate);
                      // O(1) lookup using pre-computed Map
                      const cellEvents = eventsByCell.get(cellKey) || [];
                      const jobsInCell = cellEvents.filter(
                        (ev) => ev.event.status !== 'Booking'
                      );
                      const totalJobsInCell = jobsInCell.length;
                      const expandCount =
                        totalJobsInCell -
                        (isMultiDayEvent ? 1 : topLevelJobCountMax);

                      if (expandCount >= 1) {
                        if (!vposesOverlappedByExpandButtons[i]) {
                          vposesOverlappedByExpandButtons[i] = new Set();
                        }
                        if (isMultiDayEvent) {
                          // Multi day events should not have a gap on the right side
                        } else {
                          const lastVisibleVpos = isExpanded
                            ? Math.min(
                                totalJobsInCell - 1,
                                topLevelJobCountMax + expandCount - 1
                              )
                            : topLevelJobCountMax - 1;

                          vposesOverlappedByExpandButtons[i].add(
                            lastVisibleVpos
                          );
                        }
                      }
                    }
                  }

                  // Check if there's an expand button in this cell
                  const hasExpandButtonAtEnd = (() => {
                    if (!isExpanded) {
                      // For multi-day events, only add margin on the LAST day
                      if (days > 1) {
                        const lastDayIndex = columnIndex + days - 1;
                        const expandButtonsInLastDay =
                          vposesOverlappedByExpandButtons[lastDayIndex];
                        return (
                          expandButtonsInLastDay &&
                          expandButtonsInLastDay.size > 0
                        );
                      } else {
                        // For single-day events, check if there's an expand button in this day
                        const expandButtonsInDay =
                          vposesOverlappedByExpandButtons[columnIndex];
                        return (
                          expandButtonsInDay && expandButtonsInDay.size > 0
                        );
                      }
                    } else {
                      // In expanded rows, only check if THIS specific pill has the button (original behavior)
                      return vposesOverlappedByExpandButtons[
                        columnIndex + days - 1
                      ]?.has(vpos);
                    }
                  })();

                  const widthAdjusment =
                    // right margin for expand button
                    (hasExpandButtonAtEnd
                      ? jobPillUnderExpandPillMarginRight
                      : 0) +
                    // width adjustment for overlapping jobs
                    (isExpanded ? 0 : vpos * overlappingPillOffset);

                  const pillWidth =
                    event.status === 'Booking'
                      ? floor((days - 1) * columnWidth) - jobPillMargin
                      : days * columnWidth - jobPillMargin - widthAdjusment;

                  const previewPillWidth = days * columnWidth - jobPillMargin;

                  const eventPill = (
                    <ScheduleEventPill
                      // useless key
                      key={`${event.id}:pill`}
                      event={event}
                      height={
                        event.status === 'Booking'
                          ? expandedBookingPillHeight
                          : jobPillHeight
                      }
                      width={pillWidth}
                      zoomLevel={zoomLevel}
                      selected={selectedJobPillData?.jobId === event.id}
                      filtered={event.filtered}
                    />
                  );

                  const previewPill = (
                    <ScheduleEventPill
                      // useless key
                      key={event.id + ':preview'}
                      event={event}
                      width={previewPillWidth}
                      height={jobPillHeight}
                      zoomLevel={zoomLevel}
                      filtered={event.filtered}
                    />
                  );

                  return (
                    <DraggableJobWrapper
                      key={`${event.id}:wrapper:${columnIndex}`}
                      eventDetails={{
                        id: event.id,
                        status: event.status,
                        siteId: siteId,
                        scheduledStart: new Date(event.start),
                        days: days,
                        jobName: event.summary,
                        assigneeName: event.assignee?.name || '',
                        assigneeId: event.assignee?.id,
                        scheduledStartTime: event.scheduledStartTime,
                        scheduledEndTime: event.scheduledEndTime,
                      }}
                      preview={previewPill}
                      onDragBegin={() => {
                        setDraggedSiteId(siteId);
                        setHoveredJobPill(null);
                      }}
                      onDragEnd={() => {
                        setDraggedSiteId(null);
                        setDragHoverSiteId(null);
                      }}
                      onClick={handleEventClick}
                      onMouseEnter={handleJobPillMouseEnter}
                      onMouseLeave={handleJobPillMouseLeave}
                      siteData={data.find((site) => site.id === siteId)}
                      columnIndex={columnIndex}
                      start={start}
                      isMobile={isMobile}
                      className={classNames(
                        'touch-manipulation select-none',
                        event.status === 'Booking'
                          ? 'z-[16] hover:z-[17]'
                          : 'z-0'
                      )}
                      style={{
                        position: 'absolute',
                        transform: `translate(${getColumnStart(
                          columnIndex
                        )}px, ${
                          siteTop(siteId) +
                          headerHeight +
                          (event.status === 'Booking'
                            ? 0
                            : bookingPillHeight + jobPillMargin) +
                          (isExpanded
                            ? vpos * (jobPillHeight + jobPillMargin)
                            : 0)
                        }px)`,
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        touchAction: 'manipulation',
                      }}
                    >
                      {eventPill}
                    </DraggableJobWrapper>
                  );
                }
              );
            })}

            {/* Render expand buttons - lazily computed per visible row */}
            {rowVirtualizer.getVirtualItems().flatMap((row) => {
              const rowExpandButtons = getExpandButtonsForRow(row.index);
              if (rowExpandButtons.length === 0) {
                return null;
              }

              return rowExpandButtons.map(
                (
                  {
                    siteId,
                    columnIndex,
                    expandCount,
                    isMultiDayEvent,
                    lastVisiblePillVpos,
                  },
                  expandIndex
                ) => {
                  const isRowExpanded = isSiteExpanded(siteId);

                  return (
                    <div
                      key={`${siteId}:${columnIndex}:expand:${expandIndex}`}
                      className={classNames(
                        'flex select-none flex-col items-center justify-end text-xs'
                      )}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: expandPillSize + jobPillMargin * 2,
                        height: jobPillHeight,
                        transform: `translateX(${
                          getColumnStart(columnIndex) +
                          columnWidth -
                          (expandPillSize + jobPillMargin * 2)
                        }px) translateY(${
                          headerHeight +
                          siteTop(siteId) +
                          bookingPillHeight +
                          jobPillMargin +
                          // Position on the lowest visible job pill
                          (isRowExpanded
                            ? lastVisiblePillVpos *
                              (jobPillHeight + jobPillMargin) // ✅ FIXED: Use actual last pill position
                            : isMultiDayEvent
                            ? expandPillSize + jobPillMargin // ✅ UNCHANGED: Keep collapsed positioning
                            : 0)
                        }px)`,
                        zIndex: 10, // Above job pills
                        touchAction: 'manipulation',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Simple toggle like main branch
                        if (isSiteExpanded(siteId)) {
                          collapseSite(siteId);
                        } else {
                          expandSite(siteId);
                        }
                      }}
                      onTouchStart={(e) => {
                        // Capture touch events to avoid scrolling on button press
                        e.stopPropagation();
                      }}
                    >
                      <div
                        className='flex cursor-pointer select-none items-center justify-center whitespace-nowrap rounded-md border-0.5 border-outline-default bg-surface-container-high transition-colors duration-500 hover:bg-surface-container-low'
                        style={{
                          width: expandPillSize,
                          height: expandPillSize,
                          minWidth: '23px',
                          minHeight: '23px',
                          touchAction: 'manipulation',
                          zIndex: 1,
                        }}
                      >
                        {isRowExpanded ? (
                          <FontAwesomeIcon icon={faMinus as IconProp} />
                        ) : (
                          '+' + (expandCount < 100 ? ' ' + expandCount : '')
                        )}
                      </div>
                    </div>
                  );
                }
              );
            })}

            {/* Render desktop draft job pill - extracted component */}
            {urlJobCreation &&
              isInJobCreationFlow &&
              (() => {
                const pillColumnIndex = differenceInCalendarDays(
                  urlJobCreation.parsedDate,
                  start
                );
                const draftPillVpos = 0;
                const transform = `translate(${getColumnStart(
                  pillColumnIndex
                )}px, ${
                  siteTop(urlJobCreation.siteId) +
                  headerHeight +
                  bookingPillHeight +
                  jobPillMargin +
                  draftPillVpos * (jobPillHeight + jobPillMargin)
                }px)`;

                return (
                  <DraftJobPill
                    jobName={jobName}
                    jobPillHeight={jobPillHeight}
                    columnWidth={columnWidth}
                    jobPillMargin={jobPillMargin}
                    transform={transform}
                  />
                );
              })()}

            {/* Sticky Date Headers */}
            {columnVirtualizer.getVirtualItems().map((column) => {
              // const date = add(start, { days: column.index });
              // const events = findEventsForDate(date, expanded ? 4 : 2);

              return (
                <div
                  key={`date-header-index-${column.key}`}
                  className={classNames('pointer-events-none z-[16]')}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: totalHeight,
                    width: column.size,
                    transform: `translateX(${column.start}px)`,
                  }}
                >
                  <PlannerDateHeader start={start} days={column.index} />
                </div>
              );
            })}
            {/* Fixed Side/Header/Area - shared CalendarSidebar */}
            <CalendarSidebar
              sites={sidebarSites}
              currentSiteId={undefined}
              isSidebarExpanded={isSidebarExpanded}
              setIsSidebarExpanded={setIsSidebarExpanded}
              isManuallyExpanded={isManuallyExpanded}
              setIsManuallyExpanded={setIsManuallyExpanded}
              expandedSiteIds={expandedSiteIds}
              onToggleSiteExpansion={(siteId) => {
                if (isSiteExpanded(siteId)) {
                  collapseSite(siteId);
                } else {
                  expandSite(siteId);
                }
              }}
              onSidebarMouseEnter={handleSidebarMouseEnter}
              onSidebarMouseLeave={handleSidebarMouseLeave}
              siteHeight={sidebarSiteHeight}
              navigationPath=''
              viewType='planner'
              variant='schedule'
              showDimming={false}
            />
          </div>
        </div>
        <JobPillDialogRenderer
          start={start}
          hoveredJobPill={hoveredJobPill}
          events={events}
          getSiteIndexById={getSiteIndexById}
          handlePopupMouseEnter={handlePopupMouseEnter}
          handlePopupMouseLeave={handlePopupMouseLeave}
          onCloseDialog={() => setHoveredJobPill(null)}
          floatingStyles={floatingStyles}
          setFloatingRef={floatingRefs.setFloating}
        />
      </div>
    </>
  );
}

type JobPillDialogRendererProps = {
  start: Date;
  hoveredJobPill: {
    jobId: string;
    site: PlannerViewData[number];
    date: Date;
    element: HTMLElement;
  } | null;
  events: CalendarEvent[];
  getSiteIndexById: (siteId: string) => number;
  handlePopupMouseEnter: () => void;
  handlePopupMouseLeave: () => void;
  onCloseDialog: () => void;
  /** Floating UI styles for positioning */
  floatingStyles?: React.CSSProperties;
  /** Floating UI ref setter */
  setFloatingRef?: (node: HTMLElement | null) => void;
};

/**
 * JobPillDialogRenderer - Renders the hover dialog for job pills.
 * Uses Floating UI for positioning (replacing Popper.js).
 * Memoized to prevent re-renders when parent updates unrelated state.
 */
const JobPillDialogRenderer = React.memo(function JobPillDialogRenderer({
  start,
  hoveredJobPill,
  events,
  getSiteIndexById,
  handlePopupMouseEnter,
  handlePopupMouseLeave,
  floatingStyles,
  setFloatingRef,
}: JobPillDialogRendererProps) {
  // Memoize the filtered jobs to prevent unnecessary re-computation
  const filteredJobs = useMemo(() => {
    if (!hoveredJobPill) return [];

    const columnIndex = differenceInDays(hoveredJobPill.date, start);
    const siteIndex = getSiteIndexById(hoveredJobPill.site.id);

    return events
      .filter(({ x, y }) => x === columnIndex && y === siteIndex)
      .map(({ event }) => ({
        id: event.id,
        summary: event.summary,
        assignee: event.assignee,
        scheduledStartTime: event.scheduledStartTime,
        scheduledEndTime: event.scheduledEndTime,
        start: event.start,
        end: event.end,
        status: event.status as JobStatus,
      }));
  }, [hoveredJobPill, events, start, getSiteIndexById]);

  if (!hoveredJobPill) return null;

  return (
    <FloatingPortal>
      <div
        ref={setFloatingRef}
        style={floatingStyles}
        className='pointer-events-auto z-[100]'
        onMouseEnter={handlePopupMouseEnter}
        onMouseLeave={handlePopupMouseLeave}
      >
        <CalendarDayViewDialog
          date={hoveredJobPill.date}
          site={{ id: hoveredJobPill.site.id, name: hoveredJobPill.site.name }}
          jobs={filteredJobs}
          showCreateJob={true}
        />
      </div>
    </FloatingPortal>
  );
});

JobPillDialogRenderer.displayName = 'JobPillDialogRenderer';

// PlannerView now uses the shared DraggableJobWrapper component
